// app/javascript/components/Chat/ChatMessage.js
import React from "react";
import { Box, Modal, Fade, Tooltip } from "@mui/material";
import {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
  useLayoutEffect,
} from "react";
import {
  ResponseWaiting,
  ResponseWaitingSecond,
  OnDemandWaiting,
} from "./ResponseWaiting";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import "../../../stylesheets/message.css";
import { isDarkMode } from "../../utils/constants";
import { isMobileBrowser } from "../../utils/user_agent";
import { ChartCanvas, ChartActionDropdownTabs } from "./Charting";
import ChartLayerControls from "./Charting/ChartLayerControls";
import FundamentalChart from "./Charting/FundamentalChart";
import { dedupeTickerOptions } from "./Charting/utils";
import { formatPriceUpdatedAt, railsToIana } from '../../utils/timezones';
import { ActionBox } from "./ActionBox";
import { getTickerChartData } from "../../api/message";
import DisplayData from "./DisplayData";
import { useAppContext } from "../../contexts/AppContext";
import { useChatContext } from "../../contexts/ChatContext";
import {
  useLiveDisplayInfo,
  useLiveQuoteActions,
  useLiveTickerPrices,
} from "../../contexts/LiveQuoteContext";
import LoadingPhasesOrbit from "./LoadingPhasesOrbit";
import { ImageLightbox } from "./utils";
import {
  SearchSourcesPanel,
  SearchUsageBadge,
  stripStructuredSearchSources,
} from "./SearchDecorators";
import MessageSegments from "./MessageSegments";
import MessageAttachments, { isImageAttachment } from "./MessageAttachments";
import {
  QUEUED_MESSAGE_STATUS,
  shouldRenderQueuedMessageStatus,
} from "./queuedChatMessages";
import { getChartBars } from "../../api/charting";
import { getPricingData } from "../../api/initiate_websocket_data";
import { copyContent } from "../../utils/Copy";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { motion, useAnimation } from "framer-motion";
import MainChart from "./MainChart";
import {
  PlayerCard,
  TeamCard,
  TeamVsTeamCard,
  ScoringSummaryCard,
} from "../../pages/Admin/SportsDataOverview";
import { LIVE_CHART_WEBSOCKET } from "../../utils/constants";
import {
  extendedHoursLabelForQuote,
  mergeQuoteIntoSeries,
  supportsExtendedHoursQuote,
  usEquitySessionForQuote,
} from "./liveChartQuotes";
import { createSharedRangeBus } from "./Charting/sharedRangeBus";
import {
  FUNDAMENTAL_OPTIONS,
  getAgenticChartStateFromThread,
  getPreferredScaleFromMessageThread,
  getTradeProposalFromThread,
  normalizeInlineTickerPrices,
  normalizeSupportedScale,
  parseTradeNumber,
  resolveTickerAutoSelection,
  shouldReplaceInlineTickerPrices,
} from "./chatMessageChartState";
import { shouldShowFallbackWaitingBubble } from "./chatLoadingIndicatorModel";

// How long to wait after the text stream finishes before giving up on the WS
// chart_data_recent broadcast and recovering the chart over HTTP instead. The
// broadcast normally lands within a second or two; this only fires when it is
// dropped or arrives with empty ticker_prices, which would otherwise leave the
// skeleton shimmering forever.
const CHART_SKELETON_FALLBACK_MS = 5000;

function ExpandableBubble({
  children,
  className,
  maxHeight = 300,
  isDark,
  overflowing,
  setOverflowing,
  expanded,
  setExpanded,
}) {
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const check = () => setOverflowing(el.scrollHeight > maxHeight + 1);
    check();

    let ro;
    if (window.ResizeObserver) {
      ro = new ResizeObserver(check);
      ro.observe(el);
    } else {
      const id = setInterval(check, 500);
      return () => clearInterval(id);
    }
    return () => ro && ro.disconnect();
  }, [maxHeight, children]);

  const fadeBg = isDark
    ? "linear-gradient(180deg, rgba(18,18,18,0), rgba(18,18,18,0.96))"
    : "linear-gradient(180deg, rgba(255,255,255,0), rgba(255,255,255,0.98))";

  return (
    <div className={className} style={{ position: "relative" }}>
      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? "none" : maxHeight + "px",
          overflow: "hidden",
          // keep your existing bubble typography safe
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </div>

      {!expanded && overflowing && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "72px",
            pointerEvents: "none",
            background: fadeBg,
          }}
        />
      )}

      {overflowing && (
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}
        >
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            style={{
              fontSize: "0.82rem",
              fontWeight: 600,
              padding: "6px 10px",
              borderRadius: 10,
              border: `1px solid ${isDark ? "#333" : "#e5e7eb"}`,
              background: isDark ? "rgba(255,255,255,0.03)" : "#fff",
              color: isDark ? "#f5f5f5" : "#111",
              cursor: "pointer",
            }}
          >
            {expanded ? "Show less" : "See full message"}
          </button>
        </div>
      )}
    </div>
  );
}

const SportsResponseCard = ({ payload }) => {
  if (!payload || typeof payload !== "object") return null;

  const data =
    payload.data && typeof payload.data === "object" ? payload.data : {};
  const variant =
    payload.variant ||
    payload.card_variant ||
    payload.cardVariant ||
    data.variant ||
    data.card_variant ||
    data.cardVariant;
  const league = payload.league || data.league || {};
  const team =
    payload.selected_team ||
    payload.selectedTeam ||
    data.selected_team ||
    data.selectedTeam ||
    null;
  const player =
    payload.selected_player ||
    payload.selectedPlayer ||
    data.selected_player ||
    data.selectedPlayer ||
    null;
  if (!variant || !data || Object.keys(data).length === 0) return null;

  let card = null;
  if (variant === "player_card") {
    card = <PlayerCard player={player} card={data} team={team} />;
  } else if (variant === "team_card") {
    card = <TeamCard team={team} card={data} />;
  } else if (variant === "team_v_team") {
    card = <TeamVsTeamCard team={team} matchup={data} league={league} />;
  } else if (variant === "scoring_summary") {
    card = <ScoringSummaryCard summary={data} league={league} />;
  }

  if (!card) return null;

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        "& > *": {
          width: "100%",
          boxSizing: "border-box",
        },
      }}
    >
      {card}
    </Box>
  );
};

const ChatMessage = (props) => {
  const { appProps, websocketSubscriptionRef } = useAppContext();
  const {
    loadingPhaseMessage,
    loadingPhaseDetail,
    dataProcessesQueue,
    liveTextOutputting,
    activeThread,
    setWebsocketActiveChart,
  } = useChatContext();
  const {
    getLiveQuoteSnapshot,
    setLatestTickerPrices,
    setDisplayUpdatedInfo,
  } = useLiveQuoteActions();

  const {
    idx,
    messageIndex,
    msgId,
    messageThread,
    loadingResponse,
    isLastMessage,
    isOrbAnchor,
    showLoadingWaitingMessage,
    setShowLoadingWaitingMessage,
    setModalVisible,
    setShowChatShare,
    questionOnDemand,
    preventQuestionInput,
    setPreventQuestionInput,
    onQuestionSubmit,
    secondaryExperienceType,
    isLoggedIn,
    freeUnlimitedOnly,
    hasShownFreeUnlimitedMessage,
    priorMessageThread,
    nextMessageThread,
    onBeginEditMessage,
    onSubmitEditMessage,
    onCancelEditMessage,
    editingMessage,
    onRemoveQueuedMessage,
    onRetryQueuedMessage,
    hideSendingStatus,
    suppressMessageActions,
  } = props;

  const { watchlistData, userData } = appProps;
  const userTimezone = railsToIana(userData?.user_account_info?.timezone);
  const [showAddWatchlist, setShowAddWatchlist] = useState(false);
  const [countOfWatchlistTicker, setCountOfWatchlistTicker] = useState(null);
  const showLoadingWaitingMessageRef = useRef(showLoadingWaitingMessage);
  showLoadingWaitingMessageRef.current = showLoadingWaitingMessage;
  const questionOnDemandRef = useRef(questionOnDemand);
  questionOnDemandRef.current = questionOnDemand;
  const [hasRendered, setHasRendered] = useState(false);
  const [hasRenderedModal, setHasRenderedModal] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const isMobile = isMobileBrowser();
  const preferredScaleForMessage = getPreferredScaleFromMessageThread(messageThread);
  const preferredViewForMessage =
    getAgenticChartStateFromThread(messageThread)?.view === "line"
      ? "line"
      : "bar";
  const preferredTickerIdForMessage =
    getAgenticChartStateFromThread(messageThread)?.primary_ticker_id ??
    getAgenticChartStateFromThread(messageThread)?.primaryTickerId ??
    null;
  const [selectedScale, setSelectedScale] = useState(() =>
    getPreferredScaleFromMessageThread(messageThread),
  );
  const [selectedFundamental, setSelectedFundamental] = useState("eps");
  const [availableFundamentalKeys, setAvailableFundamentalKeys] = useState(
    FUNDAMENTAL_OPTIONS.map((opt) => opt.value),
  );
  const [chartViewType, setChartViewType] = useState(preferredViewForMessage);
  const [overflowing, setOverflowing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copying, setCopying] = useState(false);
  const [editDraft, setEditDraft] = useState("");
  const editTextareaRef = useRef(null);
  // const [chartDataPresent, setChartDataPresent] = useState(false)
  const sharedRangeBusRef = useRef(null);
  if (!sharedRangeBusRef.current) {
    sharedRangeBusRef.current = createSharedRangeBus();
  }
  const sharedRangeBus = sharedRangeBusRef.current;
  // Separate bus for fundamental charts so the price chart's logical index
  // range (which has a completely different cardinality) never leaks in.
  const fundSharedRangeBusRef = useRef(null);
  if (!fundSharedRangeBusRef.current) {
    fundSharedRangeBusRef.current = createSharedRangeBus();
  }
  const fundSharedRangeBus = fundSharedRangeBusRef.current;
  const autoFundFetchKeyRef = useRef(null);
  const copyTimeoutRef = useRef(null);
  const mainChartAnim = useAnimation();
  const [isMainChartOpen, setIsMainChartOpen] = useState(true);
  const [isFundChartOpen, setIsFundChartOpen] = useState(true);
  const [showChartIndicators, setShowChartIndicators] = useState(true);
  const [mainChartInstance, setMainChartInstance] = useState(null);
  const [modalChartInstance, setModalChartInstance] = useState(null);
  const [fundChartInstance, setFundChartInstance] = useState(null);

  const [availableChartScales, setAvailableChartScales] = useState([
    { label: "1 Min", value: "1m" },
    { label: "2 Min", value: "2m" },
    { label: "3 Min", value: "3m" },
    { label: "5 Min", value: "5m" },
    { label: "10 Min", value: "10m" },
    { label: "15 Min", value: "15m" },
    { label: "30 Min", value: "30m" },
    { label: "1 Hour", value: "1h" },
    { label: "2 Hour", value: "2h" },
    { label: "3 Hour", value: "3h" },
    { label: "4 Hour", value: "4h" },
    { label: "1 Day", value: "1d" },
    { label: "1 Week", value: "1w" },
    { label: "1 Month", value: "1mo" },
  ]);
  const [scaleChanged, setScaleChanged] = useState(() =>
    getPreferredScaleFromMessageThread(messageThread),
  );
  const userScaleOverrideRef = useRef(false);
  const userTickerOverrideRef = useRef(false);
  const autoScaleRef = useRef(preferredScaleForMessage);
  const [chartOverrides, setChartOverrides] = useState(null);
  const [chartSkeletonGaveUp, setChartSkeletonGaveUp] = useState(false);
  const [selectedTickerId, setSelectedTickerId] = useState(() =>
    preferredTickerIdForMessage != null
      ? String(preferredTickerIdForMessage)
      : messageThread.answeredTickerIds?.[0] != null
        ? String(messageThread.answeredTickerIds[0])
      : null,
  );
  const [isChartLoading, setIsChartLoading] = useState(false);
  const inlineDailyTickerPrices = useMemo(
    () => normalizeInlineTickerPrices(messageThread?.tickerPrices || messageThread?.ticker_prices),
    [messageThread?.tickerPrices, messageThread?.ticker_prices],
  );
  const inlineDailyChartKey =
    selectedTickerId != null &&
    normalizeSupportedScale(selectedScale) === "1d" &&
    inlineDailyTickerPrices.length > 0
      ? `${String(selectedTickerId)}-${selectedScale}-${idx}`
      : null;

  // Keep latestTickerPrices in sync with the inline daily series when it
  // grows or when the latest daily candle changes. Phase 2/3 broadcasts a
  // recent slice early, then later payloads can replace it with either full
  // history or the same-size slice containing today's updated OHLC.
  useEffect(() => {
    if (!inlineDailyChartKey || inlineDailyTickerPrices.length === 0) return;
    const existing =
      getLiveQuoteSnapshot().latestTickerPrices?.[inlineDailyChartKey];
    if (!shouldReplaceInlineTickerPrices(existing, inlineDailyTickerPrices)) {
      return;
    }
    setLatestTickerPrices((prev) => ({
      ...(prev || {}),
      [inlineDailyChartKey]: inlineDailyTickerPrices,
    }));
  }, [
    getLiveQuoteSnapshot,
    inlineDailyChartKey,
    inlineDailyTickerPrices,
    setLatestTickerPrices,
  ]);

  useEffect(() => {
    if (!isChartModalOpen) return;
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPaddingRight = body.style.paddingRight;
    const prevHtmlOverflow = html.style.overflow;

    const scrollbarWidth = window.innerWidth - html.clientWidth;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.paddingRight = prevBodyPaddingRight;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [isChartModalOpen]);

  const queueStatus = messageThread?.queueStatus || null;
  const isQueuedMessage = Boolean(
    messageThread?.sender === "human" && queueStatus,
  );
  const currentMessageIds = useMemo(
    () =>
      [messageThread?.id, messageThread?.uuid, messageThread?.clientQueueId, msgId]
        .filter((value) => value != null && value !== "")
        .map((value) => String(value)),
    [messageThread?.clientQueueId, messageThread?.id, messageThread?.uuid, msgId],
  );
  const isEditingThis = Boolean(
    editingMessage &&
      (editingMessage.threadUuid ?? null) === (activeThread?.threadUuid ?? null) &&
      (
        (
          editingMessage.messageId != null &&
          currentMessageIds.includes(String(editingMessage.messageId))
        ) ||
        (
          Number.isInteger(editingMessage.messageIndex) &&
          editingMessage.messageIndex === messageIndex
        )
      ),
  );
  const canEditQueuedMessage =
    isQueuedMessage &&
    typeof onBeginEditMessage === "function" &&
    (
      queueStatus === QUEUED_MESSAGE_STATUS.QUEUED ||
      queueStatus === QUEUED_MESSAGE_STATUS.FAILED
    );
  const canEditThis =
    messageThread?.sender === "human" &&
    !editingMessage &&
    (
      canEditQueuedMessage ||
      (!loadingResponse && !isQueuedMessage)
    );
  const editSubmitLabel =
    queueStatus === QUEUED_MESSAGE_STATUS.QUEUED
      ? "Save"
      : queueStatus === QUEUED_MESSAGE_STATUS.FAILED
        ? "Submit"
        : "Send";
  const editStatusLabel =
    queueStatus === QUEUED_MESSAGE_STATUS.QUEUED
      ? "Queued edit"
      : queueStatus === QUEUED_MESSAGE_STATUS.FAILED
        ? "Revise and submit"
        : "Edit message";
  const queueStatusLabel =
    queueStatus === QUEUED_MESSAGE_STATUS.SENDING
      ? "Sending"
      : queueStatus === QUEUED_MESSAGE_STATUS.FAILED
        ? "Failed to send"
        : queueStatus === QUEUED_MESSAGE_STATUS.QUEUED
          ? "Queued"
          : "";
  const showQueueStatus = shouldRenderQueuedMessageStatus(queueStatus, {
    hideSending: hideSendingStatus,
  });
  const queuedMessageActionTarget =
    messageThread?.clientQueueId ||
    msgId ||
    messageThread?.id ||
    messageThread?.uuid ||
    null;
  const userActionSlotSize = 28;
  useEffect(() => {
    if (!isEditingThis) return;
    setEditDraft(editingMessage?.text ?? messageThread?.message ?? "");
  }, [isEditingThis, editingMessage?.text, messageThread?.message]);

  useLayoutEffect(() => {
    if (!isEditingThis) return;
    const el = editTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(44, el.scrollHeight)}px`;
    requestAnimationFrame(() => {
      el.focus({ preventScroll: true });
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange(len, len);
      } catch (_error) {
        // Some browsers can reject selection updates during focus handoff.
      }
    });
  }, [editDraft, isEditingThis]);

  const handleToggleChartIndicators = useCallback(() => {
    setShowChartIndicators((visible) => !visible);
  }, []);
  const handleStartEdit = () => {
    if (!canEditThis) return;
    onBeginEditMessage?.({
      idx,
      messageIndex,
      messageId: messageThread?.id || messageThread?.uuid || msgId,
      previousMessageId: messageIndex === 0 ? 0 : priorMessageThread?.id,
      text: messageThread?.message || "",
      messageThread,
    });
  };
  const handleCancelInlineEdit = () => {
    onCancelEditMessage?.();
  };
  const handleSubmitInlineEdit = () => {
    const cleaned = (editDraft || "").trim();
    if (!cleaned) return;
    onSubmitEditMessage?.(cleaned);
  };
  const isDirectThread = Boolean(activeThread?.pairedThreadId);
  const senderType = String(messageThread?.sender || "").toLowerCase();
  const priorSenderType = String(priorMessageThread?.sender || "").toLowerCase();
  const pairedUserLabel = activeThread?.pairedUserUsername || "Other User";
  let directThreadSenderLabel = null;
  if (isDirectThread && !messageThread?.isButton) {
    if (senderType === "bot") {
      if (messageThread?.senderUsername) {
        directThreadSenderLabel = messageThread.senderUsername;
      } else if (messageThread?.id != null || messageThread?.createdAt) {
        directThreadSenderLabel = pairedUserLabel;
      }
    }
  }
  const showIncomingDirectThreadSenderLabel =
    Boolean(directThreadSenderLabel) &&
    senderType === "bot" &&
    priorSenderType !== "bot";
  const senderLabelColor = isDarkMode ? "#AEB4BE" : "#596579";
  const [hasShownOrbitInCycle, setHasShownOrbitInCycle] = useState(false);
  const [orbitPhaseText, setOrbitPhaseText] = useState("");
  const [orbitPhaseDetailText, setOrbitPhaseDetailText] = useState("");
  const [hasEnteredLiveTextPhase, setHasEnteredLiveTextPhase] = useState(false);
  const [liveQuoteByTickerId, setLiveQuoteByTickerId] = useState({});

  const chartMessageThread = useMemo(() => {
    if (!chartOverrides) return messageThread;
    return { ...messageThread, ...chartOverrides };
  }, [messageThread, chartOverrides]);
  const activeChartAssetType = chartMessageThread.assetType || messageThread.assetType;
  const searchSources = useMemo(() => {
    const rawSources = Array.isArray(messageThread?.searchSources)
      ? messageThread.searchSources
      : [];
    return rawSources.filter((source) => {
      if (!source || typeof source !== "object") return false;
      return Boolean(String(source.title || "").trim() && String(source.url || "").trim());
    });
  }, [messageThread?.searchSources]);
  const searchUsed = Boolean(messageThread?.searchUsed || searchSources.length > 0);
  const displayMessageText = useMemo(
    () => stripStructuredSearchSources(messageThread?.message ?? "", searchSources),
    [messageThread?.message, searchSources],
  );
  const nextMessageText = useMemo(() => {
    const nextSources = Array.isArray(nextMessageThread?.searchSources)
      ? nextMessageThread.searchSources
      : [];
    return stripStructuredSearchSources(nextMessageThread?.message ?? "", nextSources).trim();
  }, [nextMessageThread?.message, nextMessageThread?.searchSources]);
  const nextBotMessageHasText =
    String(nextMessageThread?.sender || "").toLowerCase() === "bot" &&
    nextMessageText.length > 0;
  const responseTextHasStarted = liveTextOutputting || nextBotMessageHasText;
  const showLoadingOrbit =
    isOrbAnchor &&
    !responseTextHasStarted &&
    loadingResponse &&
    !hasEnteredLiveTextPhase &&
    Boolean(orbitPhaseText);
  const showFallbackWaitingBubble = shouldShowFallbackWaitingBubble({
    isOrbAnchor,
    loadingResponse,
    responseTextHasStarted,
    hasEnteredLiveTextPhase,
    showLoadingOrbit,
    loadingPhaseMessage,
  });

  useEffect(() => {
    if (!isOrbAnchor || !loadingResponse) {
      if (hasShownOrbitInCycle) setHasShownOrbitInCycle(false);
      if (orbitPhaseText) setOrbitPhaseText("");
      if (orbitPhaseDetailText) setOrbitPhaseDetailText("");
      if (hasEnteredLiveTextPhase) setHasEnteredLiveTextPhase(false);
      return;
    }

    if (responseTextHasStarted) {
      if (!hasEnteredLiveTextPhase) setHasEnteredLiveTextPhase(true);
      if (hasShownOrbitInCycle) setHasShownOrbitInCycle(false);
      if (orbitPhaseText) setOrbitPhaseText("");
      if (orbitPhaseDetailText) setOrbitPhaseDetailText("");
      return;
    }

    if (hasEnteredLiveTextPhase) {
      if (hasShownOrbitInCycle) setHasShownOrbitInCycle(false);
      if (orbitPhaseText) setOrbitPhaseText("");
      if (orbitPhaseDetailText) setOrbitPhaseDetailText("");
      return;
    }

    if (!loadingPhaseMessage) {
      if (hasShownOrbitInCycle) setHasShownOrbitInCycle(false);
      if (orbitPhaseText) setOrbitPhaseText("");
      if (orbitPhaseDetailText) setOrbitPhaseDetailText("");
      return;
    }

    if (!hasShownOrbitInCycle) setHasShownOrbitInCycle(true);
    setOrbitPhaseText(loadingPhaseMessage);
    setOrbitPhaseDetailText(loadingPhaseDetail || "");
  }, [
    isOrbAnchor,
    responseTextHasStarted,
    loadingResponse,
    loadingPhaseMessage,
    loadingPhaseDetail,
    hasEnteredLiveTextPhase,
    hasShownOrbitInCycle,
    orbitPhaseText,
    orbitPhaseDetailText,
  ]);

  const tickerOptions = useMemo(() => {
    if (
      Array.isArray(messageThread.answeredTickerMap) &&
      messageThread.answeredTickerMap.length > 0
    ) {
      return messageThread.answeredTickerMap
        .filter((entry) => entry && entry.id != null && entry.ticker)
        .map((entry) => ({ id: String(entry.id), label: entry.ticker }));
    }

    const ids = Array.isArray(messageThread.answeredTickerIds)
      ? messageThread.answeredTickerIds
          .filter((id) => id != null)
          .map((id) => String(id))
      : [];

    const tickers = Array.isArray(messageThread.ticker)
      ? messageThread.ticker
      : messageThread.ticker != null
        ? [messageThread.ticker]
        : [];

    if (ids.length === 0) return [];

    return ids.map((id, idx) => ({
      id,
      label: tickers[idx] || tickers[0] || `Ticker ${idx + 1}`,
    }));
  }, [
    messageThread.answeredTickerMap,
    messageThread.answeredTickerIds,
    messageThread.ticker,
  ]);

  const fetchAndApplyChartData = useCallback(
    async (tickerId, fundamentalKey) => {
      const data = await getTickerChartData(
        tickerId,
        displayMessageText,
        fundamentalKey || null,
      );
      if (!data) return;

      const availableKeys = Array.isArray(data.availableSecondaryKeys)
        ? data.availableSecondaryKeys.filter(Boolean)
        : [];

      const overrides = {
        primaryLabel: data.primaryLabel ?? messageThread.primaryLabel,
        label1: data.label1 ?? null,
        label2: data.label2 ?? null,
        label3: data.label3 ?? null,
        values1: data.values1 ?? [],
        values2: data.values2 ?? [],
        values3: data.values3 ?? [],
        logoUrls: data.logoUrls ?? [],
        availableSecondaryKeys:
          availableKeys.length > 0
            ? availableKeys
            : chartMessageThread?.availableSecondaryKeys ||
              chartMessageThread?.available_secondary_keys ||
              availableFundamentalKeys,
      };

      if (availableKeys.length > 0) {
        setAvailableFundamentalKeys(availableKeys);
        if (fundamentalKey && !availableKeys.includes(fundamentalKey)) {
          setSelectedFundamental(availableKeys[0]);
        }
      }

      setDisplayUpdatedInfo((prev) => {
        const next = { ...(prev || {}) };
        const tickerKey = parseInt(data.id, 10);
        const existing = next[tickerKey] || {};
        next[tickerKey] = {
          ...existing,
          currentPriceUpdatedAt:
            data.currentPriceUpdatedAt ?? existing.currentPriceUpdatedAt,
          currentPriceUpdatedAtIso:
            data.currentPriceUpdatedAtIso ?? existing.currentPriceUpdatedAtIso,
          pricePctChange1d:
            data.pricePctChange1d ?? existing.pricePctChange1d,
          priceChange1d:
            data.priceChange1d ?? existing.priceChange1d,
          pricePctChange24hr:
            data.pricePctChange24hr ?? existing.pricePctChange24hr,
          pricePctChangeAh:
            data.pricePctChangeAh ?? existing.pricePctChangeAh,
          currentPrice: data.currentPrice ?? existing.currentPrice,
          closePrice: data.closePrice ?? existing.closePrice,
          assetType: data.assetType ?? existing.assetType,
        };
        return next;
      });

      setChartOverrides(overrides);
    },
    [displayMessageText, messageThread.primaryLabel, setDisplayUpdatedInfo],
  );

  useEffect(() => {
    sharedRangeBus.clear("chat-reset");
  }, [
    chartViewType,
    messageThread?.id,
    messageThread?.uuid,
    sharedRangeBus,
  ]);

  useEffect(() => {
    fundSharedRangeBus.clear("chat-reset");
  }, [
    selectedScale,
    chartViewType,
    messageThread?.id,
    messageThread?.uuid,
    fundSharedRangeBus,
  ]);

  useEffect(() => {
    let cancelLog = false;

    if (preferredTickerIdForMessage != null) {
      setSelectedTickerId(String(preferredTickerIdForMessage));
    } else if (messageThread.answeredTickerIds?.[0] != null) {
      setSelectedTickerId(String(messageThread.answeredTickerIds[0]));
    } else {
      setSelectedTickerId(null);
    }

    setChartOverrides(null);
    setChartSkeletonGaveUp(false);
    autoFundFetchKeyRef.current = null;
    const msgAvailableKeys =
      messageThread.availableSecondaryKeys ||
      messageThread.available_secondary_keys;
    const initialFundKeys =
      Array.isArray(msgAvailableKeys) && msgAvailableKeys.length > 0
        ? msgAvailableKeys.filter(Boolean)
        : FUNDAMENTAL_OPTIONS.map((opt) => opt.value);
    setAvailableFundamentalKeys(initialFundKeys);
    userScaleOverrideRef.current = false;
    userTickerOverrideRef.current = false;
    autoScaleRef.current = preferredScaleForMessage;
    setSelectedScale(preferredScaleForMessage);
    setScaleChanged(preferredScaleForMessage);
    setChartViewType(preferredViewForMessage);

    return () => {
      cancelLog = true;
    };
  }, [messageThread?.id, messageThread?.uuid]);

  useEffect(() => {
    setLiveQuoteByTickerId({});
  }, [messageThread?.id, messageThread?.uuid]);

  // Phase 2/3: when answeredTickerIds populates mid-stream via the WS
  // chart_data_recent event, the existing init effect (keyed by message id /
  // uuid) doesn't re-fire, so selectedTickerId would stay null and the chart
  // wouldn't draw. Keep auto-sync for that case, but do not overwrite an
  // explicit ticker-tab choice while that ticker is still present.
  useEffect(() => {
    const selection = resolveTickerAutoSelection({
      answeredTickerIds: messageThread?.answeredTickerIds,
      selectedTickerId,
      userTickerOverride: userTickerOverrideRef.current,
    });
    userTickerOverrideRef.current = selection.userTickerOverride;
    if (!selection.shouldUpdate) return;
    setSelectedTickerId(selection.nextTickerId);
  }, [messageThread?.answeredTickerIds, selectedTickerId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleLiveQuote = (event) => {
      const detail = event?.detail || {};
      const eventIdx =
        detail?.idx == null ? null : String(detail.idx);
      if (eventIdx == null || eventIdx !== String(idx)) return;

      const quotesByTickerId = detail?.quotesByTickerId;
      if (!quotesByTickerId || typeof quotesByTickerId !== "object") return;

      setLiveQuoteByTickerId((prev) => {
        let changed = false;
        const next = { ...(prev || {}) };

        Object.entries(quotesByTickerId).forEach(([tickerId, quote]) => {
          const normalizedTickerId = String(tickerId);
          const current = next[normalizedTickerId] || {};
          const merged = {
            ...current,
            ...(quote || {}),
          };

          if (
            merged.currentPrice !== current.currentPrice ||
            merged.currentPriceValue !== current.currentPriceValue ||
            merged.currentPriceUpdatedAt !== current.currentPriceUpdatedAt ||
            merged.currentPriceUpdatedAtIso !==
              current.currentPriceUpdatedAtIso ||
            merged.pricePctChange1d !== current.pricePctChange1d ||
            merged.priceChange1d !== current.priceChange1d ||
            merged.extendedHoursPriceChange !== current.extendedHoursPriceChange ||
            merged.extendedHoursPctChange !== current.extendedHoursPctChange
          ) {
            next[normalizedTickerId] = merged;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    };

    window.addEventListener("incite:chat-live-quote", handleLiveQuote);
    return () => {
      window.removeEventListener("incite:chat-live-quote", handleLiveQuote);
    };
  }, [idx]);

  useEffect(() => {
    const lastAutoScale = autoScaleRef.current;
    if (preferredScaleForMessage === lastAutoScale) return;
    if (!userScaleOverrideRef.current && selectedScale === lastAutoScale) {
      setSelectedScale(preferredScaleForMessage);
      setScaleChanged(preferredScaleForMessage);
    }
    autoScaleRef.current = preferredScaleForMessage;
  }, [preferredScaleForMessage, selectedScale]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { imageAttachments, fileAttachments, attachmentSrcs } = useMemo(() => {
    const atts = Array.isArray(messageThread.attachments)
      ? messageThread.attachments
      : [];
    const images = [];
    const files = [];
    const imageSrcs = [];

    atts.forEach((att) => {
      const src = att?.url || att?.thumbUrl || att?.previewUrl;
      if (isImageAttachment(att)) {
        images.push(att);
        if (src) {
          imageSrcs.push(src);
        }
      } else {
        files.push(att);
      }
    });

    return {
      imageAttachments: images,
      fileAttachments: files,
      attachmentSrcs: imageSrcs,
    };
  }, [messageThread.attachments]);

  const refreshChartBars = useCallback(
    async ({ tickerId, scaleValue, force = false }) => {
      if (tickerId == null || scaleValue == null) return;

      const normalizedTickerId = String(tickerId);
      const chartKey = `${normalizedTickerId}-${scaleValue}-${idx}`;
      const existingCachedPrices =
        getLiveQuoteSnapshot().latestTickerPrices?.[chartKey];
      if (
        !force &&
        inlineDailyChartKey === chartKey &&
        inlineDailyTickerPrices.length > 0
      ) {
        if (!shouldReplaceInlineTickerPrices(existingCachedPrices, inlineDailyTickerPrices)) {
          return;
        }
        setLatestTickerPrices((prevInfo) => {
          if (!shouldReplaceInlineTickerPrices(prevInfo?.[chartKey], inlineDailyTickerPrices)) return prevInfo;
          return {
            ...(prevInfo || {}),
            [chartKey]: inlineDailyTickerPrices,
          };
        });
        return;
      }
      if (!force && existingCachedPrices) {
        return;
      }

      const bars = await getChartBars({
        tickerId: normalizedTickerId,
        scale: scaleValue,
        limit: 1500,
      });

      const liveQuoteInfo =
        getLiveQuoteSnapshot().displayUpdatedInfo?.[
          parseInt(normalizedTickerId, 10)
        ] ||
        null;
      const mappedBars = bars.map((b) => ({
        datetime: b.datetime,
        open: +b.open,
        high: +b.high,
        low: +b.low,
        close: +b.close,
        volume: +b.volume,
        vwap: b.vwap != null ? +b.vwap : null,
      }));
      const hydratedBars =
        liveQuoteInfo?.currentPriceValue != null &&
        liveQuoteInfo?.currentPriceUpdatedAtIso
          ? mergeQuoteIntoSeries({
              series: mappedBars,
              scale: scaleValue,
              price: liveQuoteInfo.currentPriceValue,
              quoteAtIso: liveQuoteInfo.currentPriceUpdatedAtIso,
              assetType: activeChartAssetType,
            })
          : mappedBars;

      setLatestTickerPrices((prevInfo) => ({
        ...(prevInfo || {}),
        [chartKey]: hydratedBars,
      }));
    },
    [
      activeChartAssetType,
      getLiveQuoteSnapshot,
      idx,
      inlineDailyChartKey,
      inlineDailyTickerPrices,
      setLatestTickerPrices,
    ],
  );

  const onScaleSelected = async (nextScale) => {
    setScaleChanged(nextScale);
    await refreshChartBars({
      tickerId: selectedTickerId,
      scaleValue: nextScale,
      force: false,
    });
  };

  useEffect(() => {
    if (selectedTickerId == null) return;
    onScaleSelected(selectedScale);
  }, [selectedTickerId, selectedScale, refreshChartBars]);

  useEffect(() => {
    if (selectedTickerId == null) return;

    if (
      chartMessageThread.answeredTickerIds &&
      chartMessageThread.answeredTickerIds.length > 0
    ) {
      const newScales = [];

      const scaleArr = [...newScales, ...availableChartScales];
      const uniqueScales = scaleArr.filter(
        (obj, index, self) =>
          index === self.findIndex((t) => t.value === obj.value),
      );
      setAvailableChartScales(uniqueScales);
    }
  }, [isLastMessage, chartMessageThread, selectedScale, selectedTickerId]);

  useEffect(() => {
    if (
      messageThread.tickerDescription &&
      messageThread.sender == "bot" &&
      messageThread.answeredTickerIds.length > 0
    ) {
      setDisplayUpdatedInfo((prev) => {
        const next = { ...(prev || {}) };
        const tickerKey = parseInt(messageThread.answeredTickerIds[0], 10);
        const existing = next[tickerKey] || {};
        next[tickerKey] = {
          ...existing,
          currentPriceUpdatedAt:
            messageThread.currentPriceUpdatedAt ?? existing.currentPriceUpdatedAt,
          currentPriceUpdatedAtIso:
            messageThread.currentPriceUpdatedAtIso ?? existing.currentPriceUpdatedAtIso,
          pricePctChange1d:
            messageThread.pricePctChange1d ?? existing.pricePctChange1d,
          priceChange1d:
            messageThread.priceChange1d ?? existing.priceChange1d,
          pricePctChange24hr:
            messageThread.pricePctChange24hr ?? existing.pricePctChange24hr,
          pricePctChangeAh:
            messageThread.pricePctChangeAh ?? existing.pricePctChangeAh,
          currentPrice: messageThread.currentPrice ?? existing.currentPrice,
          closePrice: messageThread.closePrice ?? existing.closePrice,
        };
        return next;
      });
    }
  }, [messageThread && messageThread.tickerDescription]);

  useEffect(() => {
    if (
      isLastMessage &&
      messageThread.answeredTickerIds &&
      messageThread.answeredTickerIds.length > 0
    ) {
      const ids = [];
      Object.keys(watchlistData).forEach((k) =>
        watchlistData[k].forEach((v) => ids.push(v.id)),
      );
      let inList = false;
      messageThread.answeredTickerIds.forEach((v) => {
        if (ids.includes(v)) inList = true;
      });
      if (!inList) setShowAddWatchlist(true);
      setCountOfWatchlistTicker(ids.length);
    }
  }, [isLastMessage, messageThread.answeredTickerIds, watchlistData]);

  // store all the timers we create
  const timers = useRef([]);
  useEffect(() => {
    // 1) clear any existing timers
    timers.current.forEach(clearTimeout);
    timers.current = [];
    // 2) reset immediately
    setShowLoadingWaitingMessage(0);

    // 3) schedule new ones
    if (questionOnDemand) {
      timers.current.push(
        window.setTimeout(() => {
          if (loadingResponse && isLastMessage) {
            setShowLoadingWaitingMessage("demand-1");
          }
        }, 2_000),
      );
    } else {
      // delays for the different stages
      [10_000, 16_000, 22_000, 28_000].forEach((delay, idx) => {
        timers.current.push(
          window.setTimeout(() => {
            if (loadingResponse && isLastMessage) {
              setShowLoadingWaitingMessage(idx + 1);
            }
          }, delay),
        );
      });
    }

    // 4) cleanup on dep change or unmount
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [
    loadingResponse,
    isLastMessage,
    questionOnDemand,
    setShowLoadingWaitingMessage,
  ]);

  useEffect(() => {
    if (!isLastMessage || selectedTickerId == null) return undefined;

    setWebsocketActiveChart({
      tickerId: selectedTickerId,
      idx,
      scale: selectedScale,
      assetType: activeChartAssetType,
    });

    return () => {
      setWebsocketActiveChart({
        tickerId: null,
        idx: null,
        scale: null,
        assetType: null,
      });
    };
  }, [
    activeChartAssetType,
    isLastMessage,
    selectedTickerId,
    selectedScale,
    idx,
    setWebsocketActiveChart,
  ]);

  useEffect(() => {
    if (
      !isLastMessage ||
      !activeThread?.threadUuid ||
      !userData?.current_session_id ||
      selectedTickerId == null
    ) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      getPricingData(
        activeThread.threadUuid,
        LIVE_CHART_WEBSOCKET,
        selectedTickerId,
        idx,
        selectedScale,
      );
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [
    userData?.current_session_id,
    isLastMessage,
    activeThread?.threadUuid,
    selectedTickerId,
    selectedScale,
    idx,
  ]);

  useEffect(() => {
    if (
      !isLastMessage ||
      !activeThread?.threadUuid ||
      !userData?.current_session_id ||
      selectedTickerId == null
    ) {
      return undefined;
    }

    const touchPricing = (on) => {
      const sub = websocketSubscriptionRef.current;
      if (!sub) return;
      try {
        sub.perform("set_feature", {
          data: {
            feature: "pricing",
            on,
            ttl: 30,
          },
        });
      } catch (_) {}
    };

    let refreshTimeoutId = null;
    const queueChartRefresh = () => {
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
      }
      refreshTimeoutId = setTimeout(() => {
        refreshChartBars({
          tickerId: selectedTickerId,
          scaleValue: selectedScale,
          force: true,
        }).catch((error) => {
          console.error("queueChartRefresh error", error);
        });
      }, 900);
    };

    touchPricing(true);
    queueChartRefresh();
    const intervalId = setInterval(() => touchPricing(true), 10_000);
    const onVisible = () => {
      if (!document.hidden) {
        touchPricing(true);
        queueChartRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalId);
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
      }
      document.removeEventListener("visibilitychange", onVisible);
      touchPricing(false);
    };
  }, [
    userData?.current_session_id,
    isLastMessage,
    activeThread?.threadUuid,
    selectedTickerId,
    selectedScale,
    refreshChartBars,
    websocketSubscriptionRef,
    inlineDailyChartKey,
    inlineDailyTickerPrices,
  ]);

  const handleTickerSelect = useCallback(
    async (tickerId, force = false) => {
      if (tickerId == null) return;
      const normalizedTickerId = String(tickerId);
      if (!force && selectedTickerId && normalizedTickerId === selectedTickerId)
        return;

      userTickerOverrideRef.current = true;
      setSelectedTickerId(normalizedTickerId);
      setIsChartLoading(true);
      setWebsocketActiveChart({
        tickerId: normalizedTickerId,
        idx,
        scale: selectedScale,
        assetType: activeChartAssetType,
      });

      try {
        await fetchAndApplyChartData(normalizedTickerId, selectedFundamental);
      } catch (err) {
        console.error("handleTickerSelect error", err);
      } finally {
        setIsChartLoading(false);
      }
    },
    [
      selectedTickerId,
      fetchAndApplyChartData,
      selectedFundamental,
      setWebsocketActiveChart,
      idx,
      selectedScale,
      activeChartAssetType,
    ],
  );

  const handleExpandModal = useCallback(() => {
    setIsChartModalOpen(true);
  }, []);

  const handleFundamentalSelect = useCallback(
    async (fundamentalKey) => {
      setIsFundChartOpen(true);
      setSelectedFundamental(fundamentalKey);
      if (!selectedTickerId) return;
      setIsChartLoading(true);
      try {
        await fetchAndApplyChartData(selectedTickerId, fundamentalKey);
      } catch (err) {
        console.error("handleFundamentalSelect error", err);
      } finally {
        setIsChartLoading(false);
      }
    },
    [selectedTickerId, fetchAndApplyChartData],
  );

  useEffect(() => {
    if (!availableFundamentalKeys || availableFundamentalKeys.length === 0)
      return;
    if (!availableFundamentalKeys.includes(selectedFundamental)) {
      setSelectedFundamental(availableFundamentalKeys[0]);
    }
  }, [availableFundamentalKeys, selectedFundamental]);

  useEffect(() => {
    const availableFromMessage =
      chartMessageThread?.availableSecondaryKeys ||
      chartMessageThread?.available_secondary_keys;
    if (!availableFromMessage) return;
    const filtered = availableFromMessage.filter(Boolean);
    setAvailableFundamentalKeys(filtered);
  }, [
    chartMessageThread?.availableSecondaryKeys,
    chartMessageThread?.available_secondary_keys,
  ]);

  const hasFundamental = useMemo(() => {
    return Boolean(
      chartMessageThread?.values1 &&
      chartMessageThread.values1.length > 0 &&
      chartMessageThread.label1,
    );
  }, [chartMessageThread?.label1, chartMessageThread?.values1]);

  useEffect(() => {
    // Auto-fetch default fundamental if none is present
    if (!selectedTickerId || !selectedFundamental) return;
    if (hasFundamental) return;

    const fetchKey = `${selectedTickerId}:${selectedFundamental}`;
    if (autoFundFetchKeyRef.current === fetchKey) return;
    autoFundFetchKeyRef.current = fetchKey;

    let cancelled = false;
    const run = async () => {
      setIsChartLoading(true);
      try {
        await fetchAndApplyChartData(selectedTickerId, selectedFundamental);
      } catch (err) {
        console.error("auto fundamental fetch error", err);
      } finally {
        if (!cancelled) setIsChartLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [
    selectedTickerId,
    selectedFundamental,
    hasFundamental,
    fetchAndApplyChartData,
  ]);

  const zoomHoldRef = useRef(null);
  const zoomHoldTimeoutRef = useRef(null);
  const zoomHoldingRef = useRef(false);

  const applyChartZoom = useCallback(
    (chart, factor) => {
      if (!chart?.zoom) return;
      chart.zoom(factor);
    },
    [],
  );

  const applyMainZoom = useCallback(
    (factor) => applyChartZoom(mainChartInstance, factor),
    [applyChartZoom, mainChartInstance],
  );

  const applyModalZoom = useCallback(
    (factor) => applyChartZoom(modalChartInstance, factor),
    [applyChartZoom, modalChartInstance],
  );

  const stopZoomHold = useCallback(() => {
    if (zoomHoldTimeoutRef.current) {
      clearTimeout(zoomHoldTimeoutRef.current);
      zoomHoldTimeoutRef.current = null;
    }
    if (zoomHoldRef.current) {
      clearInterval(zoomHoldRef.current);
      zoomHoldRef.current = null;
    }
    zoomHoldingRef.current = false;
  }, []);

  const startZoomHold = useCallback(
    (factor, applyFn) => (e) => {
      e.preventDefault();
      stopZoomHold();
      zoomHoldTimeoutRef.current = setTimeout(() => {
        zoomHoldingRef.current = true;
        applyFn(factor);
        zoomHoldRef.current = setInterval(() => {
          applyFn(factor);
        }, 120);
      }, 250);
    },
    [stopZoomHold],
  );

  useEffect(() => stopZoomHold, [stopZoomHold]);

  const chartActions = useMemo(() => {
    const tickerTabs = dedupeTickerOptions(tickerOptions).map((option) => ({
      label: option.label,
      value: option.id ?? option.value,
    }));

    const actions = [
      {
        key: "chartType",
        label: "Chart Type",
        value: chartViewType,
        options: [
          { label: "Line", value: "line" },
          { label: "Bar", value: "bar" },
        ],
        onChange: (val) => {
          setIsMainChartOpen(true);
          sharedRangeBus.clear("chart-type-change");
          setChartViewType(val);
        },
      },
      {
        key: "scale",
        label: "Time Scale",
        value: selectedScale,
        options: availableChartScales,
        onChange: (val) => {
          setIsMainChartOpen(true);
          userScaleOverrideRef.current = true;
          setSelectedScale(val);
        },
      },
    ];
    if (tickerTabs.length > 1) {
      actions.push({
        key: "ticker",
        label: "Ticker",
        value: selectedTickerId,
        options: tickerTabs,
        onChange: (val) => {
          setIsMainChartOpen(true);
          handleTickerSelect(val);
        },
        disabled: isChartLoading,
      });
    }
    return actions;
  }, [
    tickerOptions,
    chartViewType,
    selectedScale,
    selectedTickerId,
    availableChartScales,
    handleTickerSelect,
    setIsMainChartOpen,
    setChartViewType,
    setSelectedScale,
    isChartLoading,
    sharedRangeBus,
  ]);
  const hasMultipleTickerOptions = useMemo(
    () => dedupeTickerOptions(tickerOptions).length > 1,
    [tickerOptions],
  );

  const fundamentalActions = useMemo(
    () => [
      {
        key: "fundamentals",
        label: "Fundamentals",
        value: selectedFundamental,
        options: FUNDAMENTAL_OPTIONS.filter((opt) =>
          (availableFundamentalKeys || []).includes(opt.value),
        ),
        onChange: handleFundamentalSelect,
      },
    ],
    [selectedFundamental, handleFundamentalSelect, availableFundamentalKeys],
  );

  const handleCloseModal = useCallback(() => {
    setIsChartModalOpen(false);
    setHasRenderedModal(false);
  }, []);

  const handleModalBackdropClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        handleCloseModal();
      }
    },
    [handleCloseModal],
  );

  const handleExportCharts = useCallback(async () => {
    if (!mainChartInstance || !fundChartInstance) {
      console.warn("Charts not ready for export");
      return;
    }
    const mainCanvas = mainChartInstance.getScreenshotCanvas?.();
    const fundCanvas = fundChartInstance.getScreenshotCanvas?.();
    if (!mainCanvas || !fundCanvas) return;

    const padX = 28;
    const padYTop = 28;
    const padBetween = 24;
    const padBottom = 28;
    const headerHeight = 40;

    const width = Math.max(mainCanvas.width, fundCanvas.width) + padX * 2;
    const height =
      padYTop +
      headerHeight +
      padBetween +
      mainCanvas.height +
      padBetween +
      fundCanvas.height +
      padBottom;

    const offscreen = document.createElement("canvas");
    offscreen.width = width;
    offscreen.height = height;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = isDarkMode ? "#0f1115" : "#ffffff";
    ctx.fillRect(0, 0, width, height);

    let y = padYTop;
    const title =
      (messageThread.tickerDescription && messageThread.tickerDescription[0]) ||
      (Array.isArray(messageThread.ticker)
        ? messageThread.ticker[0]
        : messageThread.ticker) ||
      "";

    const info =
      (selectedTickerId &&
        getLiveQuoteSnapshot().displayUpdatedInfo?.[
          parseInt(selectedTickerId, 10)
        ]) ||
      {};
    const priceVal =
      info.currentPriceValue ??
      info.currentPrice ??
      info.closePrice ??
      (typeof info.price === "number" ? info.price : null);

    const currentDateTime = formatPriceUpdatedAt(
      info?.currentPriceUpdatedAt,
      info?.currentPriceUpdatedAtIso,
      userTimezone,
    );

    let textX = padX;
    let textY = y;

    ctx.font = "600 18px Inter, 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "#749EF7";
    ctx.textBaseline = "top";
    ctx.fillText(title, textX, textY);

    if (priceVal) {
      ctx.font = "600 15px Inter, 'Helvetica Neue', Arial, sans-serif";
      ctx.fillStyle = isDarkMode ? "#9ca3af" : "#374151";
      ctx.fillText(`${priceVal}`, textX, textY + 26);
    }

    if (currentDateTime) {
      ctx.font = "600 15px Inter, 'Helvetica Neue', Arial, sans-serif";
      ctx.fillStyle = "#9ca3af";
      ctx.fillText(`${currentDateTime}`, textX, textY + 52);
    }

    y += headerHeight;

    const mainX = Math.max(0, (width - mainCanvas.width) / 2);
    ctx.drawImage(mainCanvas, mainX, y);
    y += mainCanvas.height + padBetween;
    const fundX = Math.max(0, (width - fundCanvas.width) / 2);
    ctx.drawImage(fundCanvas, fundX, y);

    const url = offscreen.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = "charts.png";
    link.click();
  }, [
    mainChartInstance,
    fundChartInstance,
    isDarkMode,
    messageThread.tickerDescription,
    messageThread.ticker,
    getLiveQuoteSnapshot,
    selectedTickerId,
  ]);

  // 1) Slice the ticker data for this chart so unrelated updates don't re-render it.
  const chartDataPresent =
    chartMessageThread.answeredTickerIds &&
    chartMessageThread.answeredTickerIds.length > 0;
  // Recover a stuck chart skeleton. The WS chart_data_recent broadcast normally
  // populates answeredTickerIds and clears the skeleton, but if it never arrives
  // (dropped frame) or arrives with empty ticker_prices it is silently skipped,
  // leaving the skeleton shimmering forever. Once the text stream is done and we
  // still only hold a pending hint, wait a few seconds for the WS, then resolve:
  //   - If a chart was authoritatively intended (the answer referenced tickers),
  //     fetch the bars over HTTP — promote the hint to a real chart if they
  //     exist, otherwise drop the skeleton.
  //   - If we only ever saw the pre-LLM optimistic hint (charts may be off, or
  //     the answer simply isn't charting), never force a chart on; just drop the
  //     skeleton.
  useEffect(() => {
    const pendingIds = messageThread.pendingChartTickerIds;
    if (
      messageThread.textStreamCompleted === false ||
      chartDataPresent ||
      chartSkeletonGaveUp ||
      !Array.isArray(pendingIds) ||
      pendingIds.length === 0
    ) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!messageThread.chartAuthoritative) {
        setChartSkeletonGaveUp(true);
        return;
      }

      const primaryTickerId = String(pendingIds[0]);
      let bars = [];
      try {
        bars = await getChartBars({
          tickerId: primaryTickerId,
          scale: selectedScale,
          limit: 1500,
        });
      } catch (_error) {
        bars = [];
      }
      if (cancelled) return;

      if (!Array.isArray(bars) || bars.length === 0) {
        setChartSkeletonGaveUp(true);
        return;
      }

      const pendingTickers = Array.isArray(messageThread.pendingChartTickers)
        ? messageThread.pendingChartTickers
        : [];
      const answeredTickerIds = pendingIds.map(String);
      const answeredTickerMap = answeredTickerIds.map((id, i) => ({
        id,
        ticker: pendingTickers[i] ? String(pendingTickers[i]) : "",
      }));

      setSelectedTickerId(primaryTickerId);
      setChartOverrides((prev) => ({
        ...(prev || {}),
        answeredTickerIds,
        answeredTickerMap,
      }));
    }, CHART_SKELETON_FALLBACK_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    messageThread.textStreamCompleted,
    messageThread.pendingChartTickerIds,
    messageThread.pendingChartTickers,
    messageThread.chartAuthoritative,
    chartDataPresent,
    chartSkeletonGaveUp,
    selectedScale,
  ]);
  // 1a) Freeze the selected ticker lookup to avoid recalculating on each render.
  const selectedTickerIdInt = useMemo(() => {
    if (selectedTickerId == null) return null;
    const parsed = parseInt(selectedTickerId, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [selectedTickerId]);
  const chartTickerKey =
    selectedTickerId != null
      ? `${selectedTickerId}-${selectedScale}-${idx}`
      : null;
  const chartTickerPricesRaw = useLiveTickerPrices(chartTickerKey);
  const selectedBaseDisplayData = useLiveDisplayInfo(selectedTickerIdInt);
  const chartTickerCacheRef = useRef({});
  useEffect(() => {
    chartTickerCacheRef.current = {};
  }, [messageThread?.id, messageThread?.uuid]);
  useEffect(() => {
    if (
      !chartTickerKey ||
      !chartTickerPricesRaw ||
      !chartTickerPricesRaw.length
    )
      return;
    chartTickerCacheRef.current[chartTickerKey] = chartTickerPricesRaw;
  }, [chartTickerKey, chartTickerPricesRaw]);
  const chartTickerPrices =
    chartTickerPricesRaw ||
    (chartTickerKey && chartTickerKey === inlineDailyChartKey
      ? inlineDailyTickerPrices
      : null) ||
    (chartTickerKey ? chartTickerCacheRef.current[chartTickerKey] : null) ||
    null;
  // 1b) Derive the chart asset type once so chart renders don't depend on full displayUpdatedInfo.
  const chartAssetType = activeChartAssetType;
  const refinedQueryObj =
    messageThread?.refinedQueryObj &&
    typeof messageThread.refinedQueryObj === "object"
      ? messageThread.refinedQueryObj
      : null;
  const sportsCardPayload =
    messageThread?.sportsCardData ||
    messageThread?.sports_card_data ||
    null;
  const agenticChartState = useMemo(
    () => getAgenticChartStateFromThread(messageThread),
    [messageThread?.agenticChartState, refinedQueryObj],
  );
  const agenticOverlayKey = useMemo(() => {
    if (!agenticChartState || typeof agenticChartState !== "object") return "";
    try {
      return JSON.stringify(agenticChartState);
    } catch (_error) {
      return "";
    }
  }, [agenticChartState]);
  const tradeProposal = getTradeProposalFromThread(messageThread);
  const tradeOverlayKey = useMemo(() => {
    if (!tradeProposal || typeof tradeProposal !== "object") return "";
    const entryPrice = parseTradeNumber(
      tradeProposal.entry_price ?? tradeProposal.entryPrice,
    );
    const stopLoss = parseTradeNumber(
      tradeProposal.stop_loss ?? tradeProposal.stopLoss,
    );
    const priceTarget = parseTradeNumber(
      tradeProposal.price_target ??
        tradeProposal.priceTarget ??
        tradeProposal.profit_target ??
        tradeProposal.profitTarget,
    );
    const tickerId =
      tradeProposal.ticker_id ?? tradeProposal.tickerId ?? "";
    const anchorTime =
      tradeProposal.entry_bar_time ??
      tradeProposal.entryBarTime ??
      tradeProposal.entry_bar_datetime ??
      tradeProposal.entryBarDatetime ??
      tradeProposal.entry_bar_at ??
      tradeProposal.entryBarAt ??
      tradeProposal.current_price_updated_at ??
      tradeProposal.currentPriceUpdatedAt ??
      tradeProposal.created_at ??
      tradeProposal.createdAt ??
      "";
    return `${entryPrice ?? ""}|${stopLoss ?? ""}|${priceTarget ?? ""}|${tickerId ?? ""}|${anchorTime}`;
  }, [tradeProposal]);
  const tradeTickerLabel = useMemo(() => {
    if (!tradeProposal || typeof tradeProposal !== "object") return null;

    const direct =
      tradeProposal.ticker ||
      tradeProposal.ticker_symbol ||
      tradeProposal.tickerSymbol ||
      tradeProposal.symbol;
    if (direct) return String(direct);

    const tradeTickerId =
      tradeProposal.ticker_id ?? tradeProposal.tickerId ?? null;
    if (tradeTickerId == null) return null;
    const idStr = String(tradeTickerId);

    if (
      Array.isArray(messageThread.answeredTickerMap) &&
      messageThread.answeredTickerMap.length > 0
    ) {
      const match = messageThread.answeredTickerMap.find(
        (entry) => entry && String(entry.id) === idStr,
      );
      if (match?.ticker) return match.ticker;
    }

    const ids = Array.isArray(messageThread.answeredTickerIds)
      ? messageThread.answeredTickerIds
      : [];
    const tickers = Array.isArray(messageThread.ticker)
      ? messageThread.ticker
      : messageThread.ticker != null
        ? [messageThread.ticker]
        : [];

    const idx = ids.findIndex((id) => String(id) === idStr);
    if (idx >= 0) return tickers[idx] || tickers[0] || null;

    if (tickers.length === 1) return tickers[0];
    return null;
  }, [
    tradeProposal,
    messageThread.answeredTickerMap,
    messageThread.answeredTickerIds,
    messageThread.ticker,
  ]);
  const hasTradeProposal =
    tradeProposal &&
    typeof tradeProposal === "object" &&
    Object.keys(tradeProposal).length > 0;
  const defaultChartActionKey =
    hasTradeProposal || !hasMultipleTickerOptions ? "scale" : "ticker";
  // 1c) Build a stable config signature so ChartCanvas can memoize across parent renders.
  const chartConfigKey = useMemo(() => {
    const desc = Array.isArray(chartMessageThread.tickerDescription)
      ? chartMessageThread.tickerDescription.join("|")
      : String(chartMessageThread.tickerDescription ?? "");
    const ids = Array.isArray(chartMessageThread.answeredTickerIds)
      ? chartMessageThread.answeredTickerIds.join("|")
      : "";
    return `${chartMessageThread.primaryLabel || ""}|${desc}|${ids}|${chartAssetType || ""}`;
  }, [
    chartMessageThread.primaryLabel,
    chartMessageThread.tickerDescription,
    chartMessageThread.answeredTickerIds,
    chartAssetType,
  ]);
  const selectedDisplayData = useMemo(() => {
    if (selectedTickerIdInt == null) return null;

    const baseDisplayData = selectedBaseDisplayData;
    const liveQuoteData =
      liveQuoteByTickerId[String(selectedTickerIdInt)] || null;

    if (!baseDisplayData) return liveQuoteData;
    if (!liveQuoteData) return baseDisplayData;

    return {
      ...baseDisplayData,
      ...liveQuoteData,
    };
  }, [liveQuoteByTickerId, selectedBaseDisplayData, selectedTickerIdInt]);
  const selectedExtendedHoursQuote = useMemo(() => {
    if (!selectedDisplayData || typeof selectedDisplayData !== "object") {
      return null;
    }

    const normalizedAssetType = String(
      selectedDisplayData.assetType || chartAssetType || "",
    ).toLowerCase();
    if (!supportsExtendedHoursQuote(normalizedAssetType)) {
      return null;
    }

    const currentPrice = parseTradeNumber(
      selectedDisplayData.currentPriceValue ?? selectedDisplayData.currentPrice,
    );
    const referencePrice = parseTradeNumber(
      selectedDisplayData.closePriceValue ?? selectedDisplayData.closePrice,
    );
    if (currentPrice == null || referencePrice == null) return null;

    const quoteAtIso =
      selectedDisplayData.currentPriceUpdatedAtIso ||
      selectedDisplayData.currentPriceUpdatedAt ||
      null;
    const session = usEquitySessionForQuote(quoteAtIso);
    if (session === "regular" || session === "unknown") return null;

    return {
      price: currentPrice,
      referencePrice,
      label: extendedHoursLabelForQuote(quoteAtIso),
      quoteAtIso,
    };
  }, [chartAssetType, selectedDisplayData]);
  // 1d) Keep the DisplayData block mounted to avoid flicker while new ticker data loads.
  const displayDataValue = selectedDisplayData || {};
  // 1d) Memoize DisplayData props so we don't pass fresh objects on each render.
  const displayDataBaseProps = useMemo(
    () => ({
      messageThread,
      isLastMessage,
      showAddWatchlist,
      displayUpdatedInfo: displayDataValue,
      preventQuestionInput,
      setPreventQuestionInput,
      secondaryExperienceType,
      onQuestionSubmit,
      setModalVisible,
      setShowChatShare,
      isLoggedIn,
      countOfWatchlistTicker,
      setShowAddWatchlist,
      freeUnlimitedOnly,
      hasShownFreeUnlimitedMessage,
      onTickerSelect: handleTickerSelect,
      selectedTickerId,
      isChartLoading,
      chartMessageThread,
    }),
    [
      messageThread,
      isLastMessage,
      showAddWatchlist,
      displayDataValue,
      preventQuestionInput,
      setPreventQuestionInput,
      secondaryExperienceType,
      onQuestionSubmit,
      setModalVisible,
      setShowChatShare,
      isLoggedIn,
      countOfWatchlistTicker,
      setShowAddWatchlist,
      freeUnlimitedOnly,
      hasShownFreeUnlimitedMessage,
      handleTickerSelect,
      selectedTickerId,
      isChartLoading,
      chartMessageThread,
    ],
  );
  const displayDataModalProps = useMemo(
    () => ({
      ...displayDataBaseProps,
      showTopActions: false,
      outerMarginTop: "-40px",
      contentMarginTop: "-2px",
    }),
    [displayDataBaseProps],
  );
  // Stable style object so ChartCanvas memoization isn't busted by new object refs.
  const chartCanvasStyle = useMemo(
    () => ({ width: "100%", height: "100%", display: "block" }),
    [],
  );

  const { isButton } = messageThread;

  let leftChatClass = isDarkMode
    ? "left-chat-box dark chat-message"
    : "left-chat-box chat-message";

  let rightChatClass = "right-chat-box";
  let rightOverlapCoverClass = "right-box-overlap-cover";
  if (isDarkMode) {
    if (overflowing && !expanded) {
      rightChatClass = rightChatClass + " expandable-dark";
      rightOverlapCoverClass = rightOverlapCoverClass + " expandable-dark";
    } else {
      rightChatClass = rightChatClass + " dark";
      rightOverlapCoverClass = rightOverlapCoverClass + " dark";
    }
  } else {
    if (overflowing && !expanded) {
      rightChatClass = rightChatClass + " expandable";
      rightOverlapCoverClass = rightOverlapCoverClass + " expandable";
    }
  }
  rightChatClass = rightChatClass + " chat-message";

  // useEffect(() => {
  //   if (selectedTickerId && selectedScale && idx && latestTickerPrices[`${selectedTickerId}-${selectedScale}-${idx}`] && latestTickerPrices[`${selectedTickerId}-${selectedScale}-${idx}`].length > 0) {
  //     setChartDataPresent(true)
  //   }
  // }, [latestTickerPrices])

  const shouldOverlayLoadingIndicator =
    messageThread.sender == "human" && isOrbAnchor && loadingResponse;

  return (
    <Box
      className="chat_message_container"
      sx={{
        width: isMobile ? "96%" : "90%",
        position: shouldOverlayLoadingIndicator ? "relative" : "static",
        overflow: "visible",
      }}
    >
      {messageThread.sender == "human" && (
        <Box
          className="outgoing_container"
          sx={{
            width: "100%",
            justifyContent: "flex-end",
          }}
        >
          {!isButton && (
            <Box
              sx={{
                marginLeft: "auto",
                color: isDarkMode ? "#F5F5F5" : "#121212",
                "&:hover .msg-actions-toolbar": {
                  opacity: 1,
                  transform: "translateY(0)",
                  pointerEvents: "auto",
                },
              }}
            >
              <Box className="bubble_container" sx={{ justifyContent: "flex-end" }}>
                <ExpandableBubble
                  isDark={isDarkMode}
                  maxHeight={300}
                  className={rightChatClass}
                  overflowing={overflowing}
                  setOverflowing={setOverflowing}
                  expanded={expanded}
                  setExpanded={setExpanded}
                >
                  {isEditingThis ? (
                    <Box
                      data-chat-message-inline-editor="true"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        minWidth: isMobile ? "min(72vw, 330px)" : "340px",
                        maxWidth: "min(580px, calc(100vw - 84px))",
                      }}
                    >
                      <Box
                        component="textarea"
                        ref={editTextareaRef}
                        aria-label="Edit message"
                        value={editDraft}
                        onChange={(event) => setEditDraft(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            handleCancelInlineEdit();
                            return;
                          }
                          if (
                            event.key === "Enter" &&
                            !event.shiftKey &&
                            !isMobile
                          ) {
                            event.preventDefault();
                            handleSubmitInlineEdit();
                          }
                        }}
                        sx={{
                          width: "100%",
                          boxSizing: "border-box",
                          minHeight: "58px",
                          p: "10px 11px",
                          m: 0,
                          border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.12)"}`,
                          borderRadius: "12px",
                          outline: "none",
                          resize: "none",
                          overflow: "hidden",
                          backgroundColor: isDarkMode
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(255,255,255,0.52)",
                          color: "inherit",
                          font: "inherit",
                          lineHeight: "inherit",
                          letterSpacing: "inherit",
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                          boxShadow: isDarkMode
                            ? "inset 0 1px 0 rgba(255,255,255,0.04)"
                            : "inset 0 1px 0 rgba(255,255,255,0.7)",
                          "&:focus": {
                            borderColor: isDarkMode
                              ? "rgba(255,255,255,0.34)"
                              : "rgba(18,18,18,0.28)",
                            backgroundColor: isDarkMode
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(255,255,255,0.7)",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase",
                            color: isDarkMode
                              ? "rgba(255,255,255,0.62)"
                              : "rgba(18,18,18,0.54)",
                          }}
                        >
                          {editStatusLabel}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginLeft: "auto",
                          }}
                        >
                        <Box
                          component="button"
                          type="button"
                          onClick={handleCancelInlineEdit}
                          sx={{
                            border: 0,
                            borderRadius: "14px",
                            px: "12px",
                            py: "7px",
                            font: "inherit",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: 1,
                            cursor: "pointer",
                            color: isDarkMode ? "#F5F5F5" : "#121212",
                            backgroundColor: isDarkMode
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.08)",
                            "&:hover": {
                              backgroundColor: isDarkMode
                                ? "rgba(255,255,255,0.12)"
                                : "rgba(0,0,0,0.12)",
                            },
                          }}
                        >
                          Cancel
                        </Box>
                        <Box
                          component="button"
                          type="button"
                          disabled={!editDraft.trim()}
                          onClick={handleSubmitInlineEdit}
                          sx={{
                            border: 0,
                            borderRadius: "14px",
                            px: "12px",
                            py: "7px",
                            font: "inherit",
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: 1,
                            cursor: editDraft.trim() ? "pointer" : "not-allowed",
                            color: isDarkMode ? "#121212" : "#F5F5F5",
                            backgroundColor: isDarkMode ? "#F5F5F5" : "#121212",
                            opacity: editDraft.trim() ? 1 : 0.45,
                            "&:hover": {
                              backgroundColor: isDarkMode ? "#FFFFFF" : "#000000",
                            },
                          }}
                        >
                          {editSubmitLabel}
                        </Box>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    messageThread.message
                  )}

                  <MessageAttachments
                    imageAttachments={imageAttachments}
                    fileAttachments={fileAttachments}
                    isDark={isDarkMode}
                    onOpenLightbox={(i) => {
                      setLightboxIndex(i);
                      setLightboxOpen(true);
                    }}
                  />
                  {imageAttachments.length > 0 && (
                    <ImageLightbox
                      open={lightboxOpen}
                      onClose={() => setLightboxOpen(false)}
                      images={attachmentSrcs}
                      index={lightboxIndex}
                      setIndex={setLightboxIndex}
                    />
                  )}

                  {isQueuedMessage && showQueueStatus && (
                    <Box
                      data-chat-message-queue-status={queueStatus}
                      sx={{
                        mt: 0.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: isDarkMode
                          ? "rgba(255,255,255,0.7)"
                          : "rgba(0,0,0,0.55)",
                      }}
                    >
                      {queueStatus !== QUEUED_MESSAGE_STATUS.FAILED && (
                        <span>
                          {messageThread?.queueError || queueStatusLabel}
                        </span>
                      )}
                      {queueStatus === QUEUED_MESSAGE_STATUS.FAILED && (
                        <Tooltip title="Regenerate">
                          <IconButton
                            size="small"
                            aria-label="Regenerate message"
                            onClick={(event) => {
                              event.stopPropagation();
                              onRetryQueuedMessage?.(
                                activeThread?.threadUuid,
                                queuedMessageActionTarget,
                              );
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              color: isDarkMode
                                ? "rgba(255,255,255,0.75)"
                                : "rgba(0,0,0,0.55)",
                              "&:hover": {
                                backgroundColor: isDarkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <ReplayIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canEditQueuedMessage && (
                        <Tooltip
                          title={
                            queueStatus === QUEUED_MESSAGE_STATUS.FAILED
                              ? "Edit and submit"
                              : "Edit queued message"
                          }
                        >
                          <IconButton
                            size="small"
                            aria-label={
                              queueStatus === QUEUED_MESSAGE_STATUS.FAILED
                                ? "Edit and submit message"
                                : "Edit queued message"
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              handleStartEdit();
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              color: isDarkMode
                                ? "rgba(255,255,255,0.75)"
                                : "rgba(0,0,0,0.55)",
                              "&:hover": {
                                backgroundColor: isDarkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(
                        queueStatus === QUEUED_MESSAGE_STATUS.QUEUED
                      ) && (
                        <Tooltip
                          title="Remove queued message"
                        >
                          <IconButton
                            size="small"
                            aria-label="Remove queued message"
                            onClick={(event) => {
                              event.stopPropagation();
                              onRemoveQueuedMessage?.(
                                activeThread?.threadUuid,
                                queuedMessageActionTarget,
                              );
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              color: isDarkMode
                                ? "rgba(255,255,255,0.75)"
                                : "rgba(0,0,0,0.55)",
                              "&:hover": {
                                backgroundColor: isDarkMode
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}

                  <Box
                    className={rightOverlapCoverClass}
                    sx={{ pointerEvents: "none" }}
                  />
                </ExpandableBubble>
              </Box>

              {!suppressMessageActions && !isEditingThis && !isQueuedMessage && (
                <Box
                  className="msg-actions-row"
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    minHeight: "28px",
                    mt: "6px",
                  }}
                >
                  <Box
                    className="msg-actions-toolbar"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      px: "6px",
                      height: "28px",
                      borderRadius: "14px",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                      opacity: 0,
                      transform: "translateY(4px)",
                      transition: "opacity 150ms ease, transform 150ms ease",
                      pointerEvents: "none",
                    }}
                  >
                    <Tooltip title={copying ? "Copied!" : "Copy"}>
                      <Box
                        onClick={() => copyContent(copyTimeoutRef, setCopying, messageThread?.message)}
                        sx={{
                          position: "relative",
                          width: 26,
                          height: 26,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "background 120ms ease",
                          "&:hover": {
                            backgroundColor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <Fade in={!copying} timeout={150} unmountOnExit>
                          <Box sx={{ position: "absolute", display: "flex" }}>
                            <ContentCopyIcon sx={{ fontSize: 14, color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)" }} />
                          </Box>
                        </Fade>
                        <Fade in={copying} timeout={150} unmountOnExit>
                          <Box sx={{ position: "absolute", display: "flex" }}>
                            <CheckIcon sx={{ fontSize: 14, color: isDarkMode ? "#6ee7b7" : "#059669" }} />
                          </Box>
                        </Fade>
                      </Box>
                    </Tooltip>

                    {canEditThis && (
                      <Tooltip title="Edit message">
                        <Box
                          data-chat-message-edit-button="true"
                          onClick={handleStartEdit}
                          sx={{
                            width: 26,
                            height: 26,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "10px",
                            cursor: "pointer",
                            transition: "background 120ms ease",
                            "&:hover": {
                              backgroundColor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                            },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14, color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)" }} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      {messageThread.sender == "bot" && (
        <Box
          className="bubble_container"
          sx={{
            width: "100%",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          {showIncomingDirectThreadSenderLabel && (
            <Box
              sx={{
                width: "100%",
                display: "block",
                textAlign: "left",
                fontSize: "13px",
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "150%",
                color: senderLabelColor,
                pl: "8px",
                mb: "4px",
              }}
            >
              {directThreadSenderLabel}
            </Box>
          )}
          <Box className={leftChatClass}>
            {searchUsed && <SearchUsageBadge sources={searchSources} />}
            <MessageSegments
              msgId={msgId}
              raw={displayMessageText}
              isDark={isDarkMode}
              isStreaming={isLastMessage && loadingResponse}
            />

            <MessageAttachments
              imageAttachments={imageAttachments}
              fileAttachments={fileAttachments}
              isDark={isDarkMode}
              onOpenLightbox={(i) => {
                setLightboxIndex(i);
                setLightboxOpen(true);
              }}
            />
            {imageAttachments.length > 0 && (
              <ImageLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                images={attachmentSrcs}
                index={lightboxIndex}
                setIndex={setLightboxIndex}
              />
            )}

            {searchUsed && <SearchSourcesPanel sources={searchSources} />}

            {messageThread.textStreamCompleted !== false && (
              <SportsResponseCard payload={sportsCardPayload} />
            )}

            {/* Sized placeholder while the chart payload is en route. Only
                shown for an AUTHORITATIVE hint (chartAuthoritative) — i.e. the
                answer actually references tickers and a chart is confirmed
                coming. The pre-LLM optimistic hint fires for any ticker in the
                question, so gating on chartAuthoritative avoids flashing a
                skeleton on text-only follow-ups that never chart. The real
                chart replaces this once answeredTickerIds + prices arrive.
                Hidden while text is actively streaming (textStreamCompleted ===
                false); undefined (historical messages) does not block. */}
            {messageThread.textStreamCompleted !== false &&
              !chartDataPresent &&
              !chartSkeletonGaveUp &&
              messageThread.chartAuthoritative &&
              Array.isArray(messageThread.pendingChartTickerIds) &&
              messageThread.pendingChartTickerIds.length > 0 && (
                <Box
                  sx={{
                    marginTop: "12px",
                    marginBottom: "8px",
                    width: "100%",
                    height: "480px",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(90deg, rgba(127,127,127,0.06) 0%, rgba(127,127,127,0.12) 50%, rgba(127,127,127,0.06) 100%)",
                    backgroundSize: "200% 100%",
                    animation: "incite-chart-skeleton-shimmer 1.6s linear infinite",
                    "@keyframes incite-chart-skeleton-shimmer": {
                      "0%": { backgroundPosition: "200% 0" },
                      "100%": { backgroundPosition: "-200% 0" },
                    },
                  }}
                />
              )}

            {/* { (hasRendered && !freeUnlimitedOnly) && */}
            {/* Chart subtree gated so it stays hidden while text is actively
                streaming. Phase 2/3 data is already in messageThread (the
                chart_data_recent broadcast prefetched the bars), so the
                chart renders instantly the moment textStreamCompleted flips
                to true. Historical messages (textStreamCompleted === undefined)
                are never blocked. */}
            {chartDataPresent && messageThread.textStreamCompleted !== false && (
              <>
                {selectedTickerId && (
                  <Box sx={{ marginLeft: "0px" }}>
                    <DisplayData {...displayDataBaseProps} />
                  </Box>
                )}
                <Box
                  sx={{
                    marginLeft: "0px",
                    marginRight: "0px",
                    marginBottom: isMobile ? "-10px" : "-10px",
                    marginTop: isMobile ? "0px" : "6px",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      minHeight: "60px",
                      marginTop: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 0.5 : 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ChartActionDropdownTabs
                        actions={chartActions}
                        initialActionKey={defaultChartActionKey}
                        dropdownLabel=""
                        showDropdown={false}
                        showActionTabs
                        sx={{ width: "100%" }}
                        rightContent={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: isMobile ? 0.5 : 1,
                              width: isMobile ? 80 : 96,
                              flexShrink: 0,
                            }}
                          >
                            <IconButton
                              onClick={() => setIsMainChartOpen((v) => !v)}
                              sx={{
                                color: isDarkMode ? "#f5f5f5" : "#121212",
                              }}
                              aria-label={
                                isMainChartOpen
                                  ? "Collapse chart"
                                  : "Expand chart"
                              }
                            >
                              {isMainChartOpen ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                            {!isMobile && (
                              <Tooltip title="Expand Chart">
                                <IconButton
                                  onClick={handleExpandModal}
                                  sx={{
                                    color: isDarkMode ? "#f5f5f5" : "#121212",
                                  }}
                                  aria-label="Open chart in modal"
                                >
                                  <AspectRatioIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  </Box>

                  <motion.div
                    initial={false}
                    animate={
                      isMainChartOpen
                        ? {
                            height: "auto",
                            opacity: 1,
                            marginTop: isMobile ? 6 : 8,
                          }
                        : { height: 0, opacity: 0, marginTop: 0 }
                    }
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{ overflow: "hidden", width: "100%" }}
                  >
                    <MainChart
                      idx={idx}
                      chartMessageThread={chartMessageThread}
                      isLastMessage={isLastMessage}
                      freeUnlimitedOnly={freeUnlimitedOnly}
                      hasShownFreeUnlimitedMessage={
                        hasShownFreeUnlimitedMessage
                      }
                      isMobile={isMobile}
                      chartDataPresent={chartDataPresent}
                      hasRendered={hasRendered}
                      setHasRendered={setHasRendered}
                      selectedTickerId={selectedTickerId}
                      tradeOverlayKey={tradeOverlayKey}
                      agenticChartState={agenticChartState}
                      agenticOverlayKey={agenticOverlayKey}
                      showIndicators={showChartIndicators}
                      onToggleIndicators={handleToggleChartIndicators}
                      selectedScale={selectedScale}
                      chartViewType={chartViewType}
                      chartTickerPrices={chartTickerPrices}
                      chartAssetType={chartAssetType}
                      extendedHoursQuote={selectedExtendedHoursQuote}
                      chartConfigKey={chartConfigKey}
                      sharedRangeBus={sharedRangeBus}
                      mainChartInstance={mainChartInstance}
                      setMainChartInstance={setMainChartInstance}
                      chartCanvasStyle={chartCanvasStyle}
                      mainChartAnim={mainChartAnim}
                      handleExportCharts={handleExportCharts}
                      applyMainZoom={applyMainZoom}
                      startZoomHold={startZoomHold}
                      stopZoomHold={stopZoomHold}
                      zoomHoldingRef={zoomHoldingRef}
                      userTimezone={userTimezone}
                    />
                  </motion.div>
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        mb: 1,
                        position: "relative",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: isMobile ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ChartActionDropdownTabs
                          actions={fundamentalActions}
                          initialActionKey="fundamentals"
                          dropdownLabel="Fundamentals"
                          sx={{ width: "100%" }}
                          showDropdown={false}
                          rightContent={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 0,
                                flexShrink: 0,
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <IconButton
                                onClick={() => setIsFundChartOpen((v) => !v)}
                                sx={{
                                  color: isDarkMode ? "#f5f5f5" : "#121212",
                                }}
                                aria-label={
                                  isFundChartOpen
                                    ? "Collapse fundamental chart"
                                    : "Expand fundamental chart"
                                }
                              >
                                {isFundChartOpen ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                          }
                        />
                      </Box>
                    </Box>
                    <motion.div
                      initial={false}
                      animate={
                        isFundChartOpen
                          ? {
                              height: "auto",
                              opacity: 1,
                              marginTop: isMobile ? 6 : 8,
                            }
                          : { height: 0, opacity: 0, marginTop: 0 }
                      }
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{ overflow: "hidden", width: "100%" }}
                    >
                      <FundamentalChart
                        label={chartMessageThread.label1}
                        values={chartMessageThread.values1}
                        tickerPrices={chartTickerPrices}
                        selectedScale={selectedScale}
                        chartViewType={chartViewType}
                        sharedRangeBus={fundSharedRangeBus}
                        onChartReady={setFundChartInstance}
                        userTimezone={userTimezone}
                      />
                    </motion.div>
                  </Box>
                  <Modal
                    open={isChartModalOpen}
                    onClose={handleCloseModal}
                    keepMounted
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={handleModalBackdropClick}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: isMobile ? "90vw" : "100vw",
                          maxWidth: "1500px",
                          height: isMobile ? "90vh" : "85vh",
                          maxHeight: "90vh",
                          overflowY: "auto",
                          overflowX: "hidden",
                          boxSizing: "border-box",
                          bgcolor: isDarkMode ? "#121212" : "#ffffff",
                          borderRadius: "12px",
                          boxShadow: 24,
                          p: 2,
                          border: `1px solid ${isDarkMode ? "#333333" : "#e5e7eb"}`,
                          display: "flex",
                          flexDirection: "column",
                          scrollbarWidth: "thin",
                          "&::-webkit-scrollbar": { width: 6 },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: isDarkMode
                              ? "rgba(255,255,255,0.2)"
                              : "rgba(0,0,0,0.2)",
                            borderRadius: 8,
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            position: "sticky",
                            top: 0,
                            zIndex: 20,
                            pointerEvents: "none",
                          }}
                        >
                          <IconButton
                            onClick={handleCloseModal}
                            aria-label="Close chart modal"
                            sx={{
                              width: 40,
                              height: 40,
                              flexShrink: 0,
                              pointerEvents: "auto",
                              color: isDarkMode ? "#f5f5f5" : "#121212",
                              backgroundColor: isDarkMode
                                ? "rgba(18,18,18,0.88)"
                                : "rgba(255,255,255,0.9)",
                              border: isDarkMode
                                ? "1px solid rgba(255,255,255,0.12)"
                                : "1px solid rgba(15,23,42,0.1)",
                              boxShadow: isDarkMode
                                ? "0 8px 20px rgba(0,0,0,0.35)"
                                : "0 8px 20px rgba(15,23,42,0.14)",
                              "&:hover": {
                                backgroundColor: isDarkMode
                                  ? "rgba(38,38,38,0.95)"
                                  : "rgba(248,250,252,0.98)",
                              },
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            width: "100%",
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            marginTop: 2,
                          }}
                        >
                          {selectedTickerId && (
                            <Box sx={{ ml: "4px" }}>
                              <DisplayData {...displayDataModalProps} />
                            </Box>
                          )}
                          <Box
                            sx={{
                              position: "relative",
                              display: "flex",
                              flexDirection: isMobile ? "column" : "row",
                              justifyContent: isMobile
                                ? "center"
                                : "flex-start",
                              alignItems: isMobile ? "stretch" : "center",
                              gap: isMobile ? "10px" : "16px",
                              flexWrap: isMobile ? "wrap" : "nowrap",
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          >
                            <Box sx={{ width: "100%" }}>
                              <ChartActionDropdownTabs
                                actions={chartActions}
                                initialActionKey={defaultChartActionKey}
                                dropdownLabel=""
                                sx={{ width: "100%" }}
                                showDropdown={false}
                                showActionTabs
                              />
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              height: 518,
                              // minHeight: 360,
                              // maxHeight: 560,
                              width: "100%",
                              display: "flex",
                              position: "relative",
                              borderRadius: "14px",
                              padding: "10px 12px 8px",
                              background: isDarkMode
                                ? "linear-gradient(180deg, #0f1115 0%, #0b0d10 100%)"
                                : "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)",
                              border: isDarkMode
                                ? "1px solid #1f2937"
                                : "1px solid #e5e7eb",
                              boxSizing: "border-box",
                              flexShrink: 0,
                            }}
                          >
                            {/* 3) Render the modal chart only when the modal is open. */}
                            {isChartModalOpen && (
                              <ChartCanvas
                                idx={idx}
                                messageThread={chartMessageThread}
                                chartConfigKey={chartConfigKey}
                                hasRendered={hasRenderedModal}
                                setHasRendered={setHasRenderedModal}
                                selectedScale={selectedScale}
                                chartViewType={chartViewType}
                                tickerPrices={chartTickerPrices}
                                assetType={chartAssetType}
                                extendedHoursQuote={selectedExtendedHoursQuote}
                                isChartVisible={isChartModalOpen}
                                selectedTickerId={selectedTickerId}
                                tradeOverlayKey={tradeOverlayKey}
                                agenticChartState={agenticChartState}
                                agenticOverlayKey={agenticOverlayKey}
                                showIndicators={showChartIndicators}
                                sharedRangeBus={sharedRangeBus}
                                onChartReady={setModalChartInstance}
                                chartDataPresent={chartDataPresent}
                                canvasStyle={chartCanvasStyle}
                                userTimezone={userTimezone}
                              />
                            )}
                            <Box
                              sx={{
                                position: "absolute",
                                top: 12,
                                right: isMobile ? 52 : 64,
                                zIndex: 3,
                              }}
                            >
                              <ChartLayerControls
                                showIndicators={showChartIndicators}
                                onToggleIndicators={handleToggleChartIndicators}
                                isMobile={isMobile}
                              />
                            </Box>
                            <Box
                              sx={{
                                position: "absolute",
                                right: 12,
                                bottom: 12,
                                display: "flex",
                                gap: 1,
                                zIndex: 2,
                                backgroundColor: isDarkMode
                                  ? "rgba(15,17,22,0.7)"
                                  : "rgba(255,255,255,0.8)",
                                borderRadius: "10px",
                                border: isDarkMode
                                  ? "1px solid rgba(255,255,255,0.08)"
                                  : "1px solid rgba(0,0,0,0.08)",
                                boxShadow: isDarkMode
                                  ? "0 4px 12px rgba(0,0,0,0.45)"
                                  : "0 6px 16px rgba(15,23,42,0.12)",
                                padding: "4px 6px",
                                alignItems: "center",
                              }}
                            >
                              <IconButton
                                size="small"
                                aria-label="Zoom out"
                                onClick={() => {
                                  if (zoomHoldingRef.current) return;
                                  applyModalZoom(0.8);
                                }}
                                onPointerDown={startZoomHold(
                                  0.8,
                                  applyModalZoom,
                                )}
                                onPointerUp={stopZoomHold}
                                onPointerLeave={stopZoomHold}
                                onPointerCancel={stopZoomHold}
                                sx={{
                                  color: isDarkMode ? "#e5e7eb" : "#111827",
                                }}
                              >
                                −
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Zoom in"
                                onClick={() => {
                                  if (zoomHoldingRef.current) return;
                                  applyModalZoom(1.25);
                                }}
                                onPointerDown={startZoomHold(
                                  1.25,
                                  applyModalZoom,
                                )}
                                onPointerUp={stopZoomHold}
                                onPointerLeave={stopZoomHold}
                                onPointerCancel={stopZoomHold}
                                sx={{
                                  color: isDarkMode ? "#e5e7eb" : "#111827",
                                }}
                              >
                                +
                              </IconButton>
                            </Box>
                          </Box>
                          <Box sx={{ width: "100%", flexShrink: 0 }}>
                            <ChartActionDropdownTabs
                              actions={fundamentalActions}
                              initialActionKey="fundamentals"
                              dropdownLabel="Fundamentals"
                              sx={{ width: "100%" }}
                              showDropdown={false}
                            />
                          </Box>
                          <Box
                            sx={{
                              width: "100%",
                              height: 220,
                              flexShrink: 0,
                            }}
                          >
                            <FundamentalChart
                              label={chartMessageThread.label1}
                              values={chartMessageThread.values1}
                              tickerPrices={chartTickerPrices}
                              selectedScale={selectedScale}
                              chartViewType={chartViewType}
                              sharedRangeBus={fundSharedRangeBus}
                              height="100%"
                              outerMarginTop={0}
                              userTimezone={userTimezone}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Modal>
                </Box>
              </>
            )}

            {hasTradeProposal && (
              <Box
                sx={{
                  mt: chartDataPresent ? 2 : 1.5,
                  px: chartDataPresent && !isMobile ? "20px" : "0px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <ActionBox
                  trade={tradeProposal}
                  tickerLabel={tradeTickerLabel}
                  assetType={chartAssetType || messageThread.assetType}
                  isMobile={isMobile}
                  chatMessageId={messageThread?.id}
                />
              </Box>
            )}

            {/* <Box className={leftOverlapCoverClass} /> */}
          </Box>
        </Box>
      )}
      {(shouldOverlayLoadingIndicator || (isOrbAnchor && showLoadingOrbit)) && (
        <Box
          sx={{
            position: shouldOverlayLoadingIndicator ? "absolute" : "static",
            top: shouldOverlayLoadingIndicator ? "100%" : "auto",
            left: 0,
            right: 0,
            mt: shouldOverlayLoadingIndicator ? 0.5 : 0,
            width: "100%",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          {showFallbackWaitingBubble && (
            <Box
              sx={{
                flexDirection: "column",
                justifyContent: isMobile ? "flex-start" : "flex-end",
                width: "100%",
              }}
            >
              {showLoadingWaitingMessage == "demand-1" && (
                <OnDemandWaiting
                  text={"Thinking"}
                  sx={{ width: "152" }}
                  hideDotDotDot={true}
                />
              )}
              {showLoadingWaitingMessage == 1 && (
                <ResponseWaitingSecond
                  text={"Thinking"}
                  sx={{ width: "115px" }}
                />
              )}
              {[2, 3, 4].includes(showLoadingWaitingMessage) && (
                <ResponseWaitingSecond
                  text={"Thinking"}
                  sx={{ width: "115px" }}
                />
              )}
              {showLoadingWaitingMessage == 0 && <ResponseWaiting />}
            </Box>
          )}

          {isOrbAnchor && (
            <Fade
              in={showLoadingOrbit}
              timeout={{ enter: 320, exit: 220 }}
              mountOnEnter
            >
              <Box sx={{ width: "100%" }}>
                <LoadingPhasesOrbit
                  phaseText={orbitPhaseText}
                  phaseDetailText={orbitPhaseDetailText}
                  processQueue={dataProcessesQueue}
                />
              </Box>
            </Fade>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ChatMessage;
